<template>
  <div class="suggestions-grid-wrapper">
    <div class="suggestions__headline">
      <component :is="props.showAllResults ? 'h5' : 'h2'">
        {{ i18n.tv('suggestions', 'searchResults') }}
        <span class="suggestions__count">
          {{ props.suggestions.length }}
        </span>
      </component>
      <nuxt-link
        v-if="props.showAllResults"
        :to="resultPathWithCategory"
        class="show-all-results"
        @click="handleShowAllClick"
      >
        {{ i18n.tv('ShowAllResults', 'searchResults') }}
        <ArrowForwardIcon class="forward-icon" />
      </nuxt-link>
    </div>
    <div class="grid custom-scrollbar">
      <Grid
        :table-columns="tableColumns"
        :data-items="pagedSuggestions"
        :pageable="props.enablePagination"
        :skip="skip"
        :take="props.maxSuggestions"
        :page-size="props.maxSuggestions"
        :total="props.suggestions.length"
        :disable-paging="!props.enablePagination"
        @rowclick="onRowClick"
        @pagechange="onPageChange"
      >
        <template #suggestionRow="{props: cellProps}">
          <td class="suggestion-result-cell">
            <div class="suggestion-result">
              <div class="suggestion-left">
                <span class="suggestion-icon-left"><CustomSku /></span>
                <span
                  class="customer-sku-left"
                  v-html="highlightSearchTerm(cellProps.dataItem.CustomerSku)"
                />
              </div>
              <div class="suggestion-right">
                <div class="suggestion-right__sku-stock">
                  <span class="customer-sku-right">{{ cellProps.dataItem.Sku }}</span>
                  <div
                    v-if="shouldShowAvailability() && !isAvailabilityLoaded(cellProps.dataItem)"
                    class="suggestion__stock suggestion__stock--skeleton"
                  >
                    <Skeleton
                      shape="circle"
                      class="stock-skeleton__circle"
                    />
                    <Skeleton
                      shape="rectangle"
                      class="stock-skeleton__line"
                    />
                  </div>
                  <div
                    v-else-if="shouldShowAvailability()"
                    class="suggestion__stock"
                  >
                    <ImageCircle
                      v-if="isAvailable(cellProps.dataItem)"
                      class="svg-image-circle icon--svg circle--available"
                    />
                    <OutlineCircle
                      v-else
                      class="svg-outline-circle icon--svg circle--not-available"
                    />
                    <span
                      class="stock-quantity"
                      v-text="getCurrentStock(cellProps.dataItem)"
                    />
                  </div>
                </div>
                <a
                  href="javascript:void(0)"
                  class="suggestion-buybox-icon"
                  @click.prevent.stop="handleBuyboxClick(cellProps.dataItem.Sku, cellProps.rowIndex)"
                >
                  <ActionShoppingCart
                    class="svg-action-shopping-cart"
                    :title="i18n.tv('searchShoppingCartIconTooltip', 'users')"
                  />
                </a>
              </div>
            </div>
          </td>
        </template>
        <template #emptyHeader>
          <!-- Completely empty header -->
        </template>
      </Grid>
    </div>
  </div>
</template>

<script setup>
import Grid from '~/components/grid/Grid'
import ActionShoppingCart from '~/assets/icons/action_shopping_cart_plus.svg?component'
import CustomSku from '~/assets/icons/custom_sku.svg?component'
import ImageCircle from '~/assets/icons/image_circle.svg?component'
import OutlineCircle from '~/assets/icons/outline_circle.svg?component'
import useI18nExtended from '~/composables/usei18nExtended'
import {useProductService} from '~/composables/product_service'
import {useRightsHelper} from '~/composables/rights_helper'
import {PROPERTY_SCOPE_NAMES, USER_RIGHTS_MAPPING} from '~/assets/js/constants'
import ArrowForwardIcon from '~/assets/icons/md_arrow_forward.svg?component'
import {Skeleton} from '@progress/kendo-vue-indicators'
import {ref, computed} from 'vue'

const props = defineProps({
  suggestions: {type: Array, default: () => []},
  maxSuggestions: {type: Number, default: 4},
  showAllResults: {type: Boolean, default: false},
  enablePagination: {type: Boolean, default: false},
  searchTerm: {type: String, default: ''},
  searchFilter: {type: Array, default: () => []},
  l2categories: {type: Array, default: () => []},
  availabilities: {type: Array, default: () => []},
  loadedAvailabilitySkus: {type: Array, default: () => []}
})

const emit = defineEmits(['suggestion-clicked', 'buybox-clicked', 'show-all-results'])
const i18n = useI18nExtended()
const router = useRouter()
const nuxtApp = useNuxtApp()
const {userHasRight} = useRightsHelper()
const {getProductBySkuNew} = useProductService()

const skip = ref(0)

const pagedSuggestions = computed(() =>
  props.enablePagination
    ? props.suggestions.slice(skip.value, skip.value + props.maxSuggestions)
    : props.suggestions.slice(0, props.maxSuggestions)
)

function onPageChange(event) {
  skip.value = event.page.skip
}

const resultPath = computed(() => {
  const path = nuxtApp.$globalization.localePath({name: 'searchResults'})
  return path + '?query=' + encodeURIComponent(props.searchTerm)
})

const resultPathWithCategory = computed(() => {
  let resultingPath = resultPath.value
  if (props.l2categories && props.l2categories.length === 1) {
    resultingPath += `&PrimaryL2Category=${props.l2categories[0].categoryName}`
  }
  if (!Array.isArray(props.searchFilter) || props.searchFilter.length === 0) {
    return resultingPath
  }
  let catFilter = ''
  props.searchFilter.forEach(filter => {
    if (!catFilter) {
      catFilter = `ProductDesign=${filter}`
    } else {
      catFilter += `%24${filter}`
    }
  })
  return resultingPath + '&' + catFilter
})

const tableColumns = [
  {
    field: 'CustomerSku',
    title: '',
    cell: 'suggestionRow',
    width: '100%',
    sortable: false,
    headerTemplate: '',
    headerCell: 'emptyHeader'
  }
]

const onRowClick = async (event) => {
  const suggestion = event.dataItem
  try {
    const product = await getProductBySkuNew(
      suggestion.Sku,
      nuxtApp.$globalization.getRegion(),
      i18n.locale.value,
      false,
      null,
      PROPERTY_SCOPE_NAMES.DESIGN_ONLY
    )
    if (product && product.productDesign) {
      const localized = nuxtApp.$globalization.localePath(`/products/${suggestion.Sku}`, i18n.locale.value)
      await router.push(localized)
    } else {
      emit('suggestion-clicked', {sku: suggestion.Sku, index: event.index})
    }
  } catch {
    emit('suggestion-clicked', {sku: suggestion.Sku, index: event.index})
  }
}

const handleBuyboxClick = (sku, index) => {
  emit('buybox-clicked', {sku, index})
}

const highlightSearchTerm = (text) => {
  const term = props.searchTerm
  if (!term) { return text }
  const escapedTerm = term.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escapedTerm})`, 'gi')
  return text.replace(regex, '<span class="highlight">$1</span>')
}

function handleShowAllClick() {
  emit('show-all-results')
}

function isAvailabilityLoaded(suggestion) {
  return props.loadedAvailabilitySkus.some(sku => String(sku || '').toLowerCase() === String(suggestion?.Sku || '').toLowerCase())
}

function getAvailability(suggestion) {
  return userHasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY)
    ? props.availabilities?.find(a => String(a?.sku || '').toLowerCase() === String(suggestion?.Sku || '').toLowerCase()) ?? null
    : null
}

function getCurrentStock(suggestion) {
  const availability = getAvailability(suggestion)
  if (!userHasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY) || !availability?.availabilities) {
    return '-'
  }

  const totalStock = availability.availabilities
    .filter(a => !a.plantIsDisplayOnly)
    .reduce((sum, a) => sum + (a.currentStock?.quantity || 0), 0)

  const unit = availability.availabilities?.find(a => a.currentStock?.unit)?.currentStock?.unit

  return totalStock > 0 ? getQuantityDisplay(totalStock, unit) : '-'
}

const isAvailable = (suggestion) => {
  const availability = getAvailability(suggestion)
  if (!availability?.availabilities) {
    return false
  }
  const totalStock = availability.availabilities
    .filter(a => !a.plantIsDisplayOnly)
    .reduce((sum, a) => sum + (a.currentStock?.quantity || 0), 0)
  return totalStock > 0
}

const getQuantityDisplay = (quantity, unit) => {
  const formattedQuantity = quantity.toLocaleString(i18n.locale.value === 'en' ? 'en' : 'de')
  return i18n.tv({messageKey: 'quantityAndUnit', messageValues: {quantity: formattedQuantity, unit: i18n.tv(unit, 'packageUnits')}})
}

const shouldShowAvailability = () => {
  return userHasRight(USER_RIGHTS_MAPPING.PRODUCT_VIEW_AVAILABILITY)
}
</script>

<style lang="scss">
.suggestions-grid-wrapper {
  margin-top: 1.5rem;

  .suggestions__headline {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: .5rem;

    h5 {
      font-weight: $base-text-font-weight-bold;
      margin: 0;
      display: flex;
      align-items: center;

    }

    h2 {
      margin: 2rem 0 .5rem;
    }

    .k-button {
      font-size: 0.875rem;
      padding: 0.5rem 1rem;
      text-transform: none;
    }

    .show-all-results {
      display: flex;
      align-items: center;
      gap: .25rem;
      cursor: pointer;
      font-weight: $base-text-font-weight-semi-bold;

      .forward-icon {
        display: inline-block;
        width: 1.25rem;
        height: 1.25rem;
        flex-shrink: 0;
        color: $color__fst__cyan__100;
      }
    }
  }

  .suggestions__count {
    padding-left: .25rem;
  }

  .k-grid {
    border-color: $color__fst__white;

    td {
      >div {
        display: flex;
        justify-content: space-between;
        min-height: 1.75rem;
        align-items: center;
      }
    }

    .k-grid-table-wrap  {
      table {
        width: 100% !important;

        tbody > tr:nth-child(2) {
            background-color: $color__fst__error;
        }
      }

      .suggestion-right {
        display: flex;
        gap: 1rem;
        align-items: center;
        cursor: pointer;

        &__sku-stock {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }

        span {
          display: flex;
          align-content: center;
          font-size: .875rem;
        }

        a {
          display: flex;
          align-items: center;

          svg {
            width: 1.8rem;
            height: 1.8rem;
            color: $color__fst__primary;
          }
        }
      }

      .suggestion__stock {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;

        .svg-image-circle,
        .svg-outline-circle {
          font-size: .8rem;
          padding-right: .2rem;
        }

        .circle--available {
          color: $color--input__border--ok;
          padding-bottom: .25rem;
        }

        .circle--not-available {
          color: $color--input__border--error;
        }

        .stock-quantity {
          font-weight: $base-text-font-weight-light;
          font-size: .75rem;
          white-space: nowrap;
        }

        &--skeleton {
          display: flex;
          align-items: center;
          gap: .2rem;

          .stock-skeleton__circle {
            width: .75rem;
            height: .75rem;
          }

          .stock-skeleton__line {
            width: 2.5rem;
            height: .6rem;
          }
        }

        @include breakpoint-down($md) {
          display: none;
        }
      }

      .suggestion-left {
        display: flex;
        gap: 1.25rem;
        align-items: center;

        .suggestion-icon-left {
          display: flex;
          align-items: center;

          svg {
            width: 1.5rem;
            height: 1.5rem;
          }
        }

        .customer-sku-left {
          font-size: .875rem;
          color: $color__fst__primary;

          .highlight {
            font-weight: $base-text-font-weight-bold;
          }
        }
      }
    }

    .k-grid-content {
      overflow-y: hidden !important;

      &::-webkit-scrollbar {
        display: none;
      }

      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    .k-grid-container {
      .k-grid-content {
        .k-grid-table {
          .k-master-row.k-alt {
            background-color: $color__fst__white;
          }

          .k-master-row>td {
            border-bottom-width: .0625rem;
          }

          .k-master-row {
            cursor: pointer;
          }
        }
      }
    }
  }

  .k-grid .k-grid-header {
    display: none;
  }

  .k-pager {
    background-color: $color__fst__white;
    border-top: .1875rem solid $color__fst__gray__020;
  }

  .k-grid-table .k-master-row:last-child > td { border-bottom-width: 0 !important; }
}

@include breakpoint-down($md) {
  .suggestions-grid-wrapper {
    .k-grid,
    .k-grid-table-wrap,
    .k-grid-table-wrap table,
    .k-grid-table-wrap tbody,
    .k-grid-table-wrap tr,
    .k-grid-table-wrap td {
      display: block !important;
      width: calc(100% - .6rem);
    }

    .grid .k-grid table  {
      margin-bottom: 0;

      tr {
        margin-bottom: 0;
        box-shadow: none;
        border-bottom: .0625rem solid $color__fst__gray__020;

        td {
          margin-bottom: 0;
        }
      }
    }

    .suggestion-result {
      display: flex !important;
      justify-content: space-between !important;
      align-items: center !important;
    }
  }

  .search-suggestions {
    margin: 0;

    .suggestions__headline {
      margin-left: 1rem;
    }
  }
}
</style>
